import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { supabase } from '@/integrations/supabase/client';

/**
 * Hook to batch-fetch location grants for multiple listings.
 * Returns a map of listingId -> hasLocationAccess.
 * Used by marketplace to show listings with hidden locations on map
 * when the current user has been granted access.
 */
export function useLocationGrantsMap(listingIds: string[]) {
  const { user } = useAuth();
  const [grantsMap, setGrantsMap] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);

  // Memoize the listingIds to prevent unnecessary refetches
  const stableListingIds = useMemo(() => {
    return listingIds.sort().join(',');
  }, [listingIds]);

  useEffect(() => {
    if (!user || listingIds.length === 0) {
      setGrantsMap({});
      return;
    }

    const fetchGrants = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('info_access_grants')
          .select('listing_id, granted_fields')
          .eq('granted_to_user_id', user.id)
          .in('listing_id', listingIds)
          .or('expires_at.is.null,expires_at.gt.now()');

        if (error) {
          console.error('Error fetching location grants:', error);
          setGrantsMap({});
          return;
        }

        // Build map: listingId -> hasLocationGrant
        const map: Record<string, boolean> = {};
        data?.forEach((grant) => {
          const fields = grant.granted_fields as string[];
          if (fields?.includes('location')) {
            map[grant.listing_id] = true;
          }
        });
        setGrantsMap(map);
      } catch (err) {
        console.error('Error in useLocationGrantsMap:', err);
        setGrantsMap({});
      } finally {
        setLoading(false);
      }
    };

    fetchGrants();

    // Subscribe to realtime changes on info_access_grants
    const channel = supabase
      .channel('location-grants-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'info_access_grants',
          filter: `granted_to_user_id=eq.${user.id}`,
        },
        () => {
          // Refetch when grants change
          fetchGrants();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, stableListingIds]);

  return { grantsMap, loading };
}
